import React, { FC, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { CcIcon } from '@utp/components-react';

import {
  MainContainer,
  ResumenGeneralContainer,
  DownloadButton,
  CicloCursosContainer,
  CicloHeader,
  StatusBadge
} from './NotesRecordViewStyled';
import PdfDocument from './components/PdfDocument';
import downloadIcon from '../../../../../assets/icons/Icon-download.svg';
import TextHelper from '@/core/helpers/TextHelper';
import { SubsectionHeader } from '@/presentation/reusables/SectionHeader';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';

interface Course {
  cycle: string;
  courseName: string;
  grade: string;
  approvalStatus: string;
  catalogNumber: string;
  numberTimes: string;
  weeklyHours: string;
  credits: string;
  section: string;
}

interface Period {
  name: string;
  items: Course[];
}

interface IProps {
  handlerBack: () => void;
  data: {
    studentName: string;
    studentCode: string;
    progDesc: string;
    plan: string;
    totalApprovedCredits: number;
    periods: Period[];
  };
  subsectionHeader: any;
}

const NotesRecordView: FC<IProps> = ({
  handlerBack,
  data,
  subsectionHeader
}) => {
  const [expandedSections, setExpandedSections] = useState<boolean[]>(
    Array(data.periods.length).fill(false)
  );

  const toggleSection = (index: number) => {
    setExpandedSections((prevState) => {
      const newState = [...prevState];
      newState[index] = !newState[index];
      return newState;
    });
  };

  const handleDownload = async () => {
    GoogleAnalytics.writeLog('portal-course-downloadRecordNotes', 'course');
    const blob = await pdf(
      <PdfDocument
        academicPlan={data.plan}
        data={data.periods}
        fullName={data.studentName}
        studentCode={data.studentCode}
        program={data.progDesc}
        totalCredits={data.totalApprovedCredits}
      />
    ).toBlob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'record_de_notas.pdf';
    link.click();
  };

  const renderTableHeaders = () => (
    <thead
      style={{
        position: 'sticky',
        top: 0,
        backgroundColor: '#fff',
        zIndex: 1
      }}
    >
      <tr>
        {[
          'Ciclo',
          'Código de curso',
          'Curso',
          'Número de vez',
          'Horas semanales',
          'Créditos',
          'Nota',
          'Sección',
          'Estado'
        ].map((header) => (
          <th key={header} className="table-header">
            {header}
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderTableRows = (course: Course, index: number) => (
    <tr key={index}>
      <td className="table-cell">{course.cycle}</td>
      <td className="table-cell">{course.catalogNumber}</td>
      <td className="table-cell">
        {TextHelper.fixRomans(
          TextHelper.capitalize(course.courseName, false, true)
        )}
      </td>
      <td className="table-cell">
        {course.numberTimes === 'null' ? '--' : course.numberTimes}
      </td>
      <td className="table-cell">{course.weeklyHours}</td>
      <td className="table-cell">{course.credits}</td>
      <td className="table-cell">
        {course.grade === ' ' ? '--' : course.grade}
      </td>
      <td className="table-cell">{course.section}</td>
      <td className="table-cell status-cell">
        <StatusBadge status={course.approvalStatus}>
          {course.approvalStatus.replace('Curso', 'curso')}
        </StatusBadge>
      </td>
    </tr>
  );

  return (
    <MainContainer>
      <div className="container">
        <div>
          <SubsectionHeader
            title={subsectionHeader[1].title}
            onClick={handlerBack}
          />

          <div className="content">
            <ResumenGeneralContainer>
              <h3 className="resumen-general-title">Resumen general</h3>
              <div className="line" />

              <div className="resumen-general-content">
                <div className="row">
                  <div>
                    <span className="resumen-label">Alumno:</span>{' '}
                    {TextHelper.capitalize(data.studentName)}
                  </div>
                  <div>
                    <span className="resumen-label">Código:</span>{' '}
                    {data.studentCode}
                  </div>
                  <div>
                    <span className="resumen-label">Programa:</span>{' '}
                    {TextHelper.capitalize(data.progDesc)}
                  </div>
                </div>

                <div className="row">
                  <div>
                    <span className="resumen-label">Plan de estudio:</span>{' '}
                    {data.plan}
                  </div>
                  <div>
                    <span className="resumen-label">Resumen de créditos:</span>
                    {' Aprobado '}
                    {data.totalApprovedCredits.toFixed(2)}
                  </div>
                </div>
              </div>
            </ResumenGeneralContainer>

            <DownloadButton onClick={handleDownload}>
              <img src={downloadIcon} alt="Descargar" />
              <span>Descargar</span>
            </DownloadButton>
          </div>

          {data.periods.map((period, index) => (
            <CicloCursosContainer key={period.name}>
              <div
                style={{
                  overflowX: 'auto',
                  width: '100%'
                }}
              >
                <CicloHeader onClick={() => toggleSection(index)}>
                  <div
                    style={{
                      fontFamily: 'Lato',
                      fontSize: '14px',
                      fontWeight: 900,
                      lineHeight: '20px',
                      textAlign: 'left',
                      color: '#161D1F'
                    }}
                  >
                    {period.name}
                  </div>
                  <span className="toggle-icon">
                    {expandedSections[index] ? (
                      <CcIcon name="chevron-up" size={24} color="#5B36F2" />
                    ) : (
                      <CcIcon name="chevron-down" size={24} color="#5B36F2" />
                    )}
                  </span>
                </CicloHeader>

                {expandedSections[index] && (
                  <div
                    className="table-wrapper"
                    style={{
                      maxHeight: period.items.length > 6 ? '330px' : 'auto',
                      overflowY: 'auto',
                      marginLeft: '12px',
                      marginRight: '12px'
                    }}
                  >
                    <table>
                      {renderTableHeaders()}
                      <tbody>{period.items.map(renderTableRows)}</tbody>
                    </table>
                  </div>
                )}
              </div>
            </CicloCursosContainer>
          ))}
        </div>
      </div>
    </MainContainer>
  );
};

export default NotesRecordView;
